import React, { useCallback, useContext, useMemo } from 'react'

import { Input, Radio, Tabs, Tooltip } from 'antd'
import { isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import SmartTable, { SmartTableDataTypes } from '../../shared/SmartTable'
import { onError, onSuccess } from '../../../utils/apiHelper'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { InstitutionContext } from '../../../Providers/InstitutionProvider'
import { createRejectionReason, deleteRejectionReason, updateRejectionReason } from '../../../utils/api/rejectionReason'
import { isObject } from '../../../utils'
import { isNurse } from '../../../utils/roles'

const RadioGroup = Radio.Group
const TabPane = Tabs.TabPane
const TextArea = Input.TextArea

const RADIO_MAX_WIDTH = '450px'

export const OTHER_REJECTION_REASON = 0
export const REFUSAL_TAB = '1'

const RejectionReasonsTabs = ({ selectedReason, comment, commentMaxLength, onSelectedReasonChange, onCommentChange, onTabChange }) => {
  const ic = useContext(InstitutionContext)

  const institution = useMemo(() => ic?.institution ?? null, [ic?.institution])
  const rejectionReasons = useMemo(() => ic?.rejectionReasons ?? [], [ic?.rejectionReasons])
  const setRejectionReasons = useCallback(reasons => {
    if (isObject(ic) && isFunction(ic?.setRejectionReasons)) {
      ic.setRejectionReasons(reasons)
    }
  }, [ic?.setRejectionReasons])

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const handleDatacreation = (reason) => {
    createRejectionReason(user, { institution: institution.id, name: reason.name }).then(({ data }) => {
      setRejectionReasons(oldReasons => [...oldReasons, data])
      onSuccess(t('rejection_reason.create.success'))
    }).catch((error) => {
      onError(t('rejection_reason.create.error'))
      console.error(error)
    })
  }

  const handleDataEdition = (reason) => {
    updateRejectionReason(user, reason, { name: reason.name }).then(({ data }) => {
      setRejectionReasons(oldReasons => oldReasons.map(r => (r.id === data.id ? { ...r, name: data.name } : r)))
      onSuccess(t('rejection_reason.update.success'))
    }).catch((error) => {
      onError(t('rejection_reason.update.error'))
      console.error(error)
    })
  }

  const handleDataDeletion = (reason) => {
    deleteRejectionReason(user, reason).then(() => {
      setRejectionReasons(oldReasons => oldReasons.filter(r => r.id !== reason.id))
      onSuccess(t('rejection_reason.delete.success'))
    }).catch((error) => {
      onError(t('rejection_reason.delete.error'))
      console.error(error)
    })
  }

  const renderRejectionReasons = () => {
    const reasons = rejectionReasons.map(rr => {
      return (
        <Radio
          key={rr.id}
          style={{
            display: 'block',
            overflow: 'hidden',
            maxWidth: RADIO_MAX_WIDTH,
            textOverflow: 'ellipsis'
          }}
          value={rr.id}
        >
          <Tooltip placement='left' title={rr.name}>
            {rr.name}
          </Tooltip>
        </Radio>
      )
    })

    return (
      <div>
        {reasons}
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Radio style={{ marginTop: '5px' }} value={OTHER_REJECTION_REASON} />
          <TextArea
            autoSize
            placeholder={t('Other')}
            value={comment}
            maxLength={commentMaxLength}
            onChange={v => onCommentChange(v.target.value)}
          />
        </div>
      </div>
    )
  }

  const columns = [
    { type: SmartTableDataTypes.ID, key: 'id' },
    { type: SmartTableDataTypes.STRING, name: t('Reason'), key: 'name' }
  ]
  const rejections = useMemo(() => rejectionReasons.map(s => ({
    id: s.id,
    name: s.name
  })), [rejectionReasons])

  return (
    <Tabs defaultActiveKey={REFUSAL_TAB} onChange={onTabChange} animated={false}>
      <TabPane tab={t('Refuse an internship')} key={REFUSAL_TAB}>
        {t('Please select a reason to refuse this internship')} :
        <div className='v-spacing' />
        <RadioGroup style={{ width: '100%' }} onChange={v => onSelectedReasonChange(v.target.value)} value={selectedReason}>
          {renderRejectionReasons()}
        </RadioGroup>
        {rejectionReasons.length === 0 && (
          <div style={{ marginTop: '30px', color: '#9E9E9E' }}>
            {t('You can add rejection reasons in the second tab.')}
          </div>
        )}
      </TabPane>
      {!isNurse(user) && (
        <TabPane tab={t('Rejection reasons')} key='2'>
          <SmartTable
            columns={columns}
            data={rejections}
            loading={ic?.loading}
            noDataText={t('You do not have any internship rejection reason.')}
            onDataAdd={handleDatacreation}
            onDataDelete={handleDataDeletion}
            onDataEdit={handleDataEdition}
          />
        </TabPane>
      )}
    </Tabs>
  )
}

export default RejectionReasonsTabs
